
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Helmet } from "react-helmet-async";
import { useUserProfile } from "@/hooks/useUserProfile";
import { Loader2, RotateCcw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CALIBRATION_KEYS, PREFERENCE_KEYS, SlicerType } from "@/constants/calibrationKeys";
import PrinterManagement from "@/components/profile/PrinterManagement";
import SecuritySettings from "@/components/profile/SecuritySettings";
import UsernameSettings from "@/components/profile/UsernameSettings";
import ProfilePictureSettings from "@/components/profile/ProfilePictureSettings";
import SimplePrivacyToggle from "@/components/profile/SimplePrivacyToggle";

const PreferencesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, setLanguage } = useUserProfile();
  const { toast } = useToast();
  const [savedMode, setSavedMode] = useState<string | null>(null);
  const [savedSlicer, setSavedSlicer] = useState<SlicerType | null>(null);

  useEffect(() => {
    setSavedMode(localStorage.getItem(PREFERENCE_KEYS.calculatorMode));
    const slicer = localStorage.getItem(PREFERENCE_KEYS.slicer);
    if (slicer === "creality" || slicer === "orca" || slicer === "anycubic") {
      setSavedSlicer(slicer);
    }
  }, []);

  const resetCalibrationProgress = () => {
    Object.values(CALIBRATION_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    toast({
      title: "Progress Reset",
      description: "All calibration progress has been cleared.",
    });
  };

  const clearModePreference = () => {
    localStorage.removeItem(PREFERENCE_KEYS.calculatorMode);
    setSavedMode(null);
    toast({
      title: "Preference Cleared",
      description: "Mode preference has been removed.",
    });
  };

  const clearSlicerPreference = () => {
    localStorage.removeItem(PREFERENCE_KEYS.slicer);
    setSavedSlicer(null);
    toast({
      title: "Preference Cleared",
      description: "Slicer preference has been removed.",
    });
  };

  const handleSlicerChange = (slicer: SlicerType) => {
    localStorage.setItem(PREFERENCE_KEYS.slicer, slicer);
    setSavedSlicer(slicer);
    toast({
      title: "Preference Saved",
      description: `${getSlicerDisplayName(slicer)} has been set as your preferred slicer.`,
    });
  };

  const getModeDisplayName = (mode: string) => {
    return mode === "fast" ? "Fast Mode" : "Tutorial Mode";
  };

  const getSlicerDisplayName = (slicer: string) => {
    const names = {
      creality: "Creality Print",
      orca: "Orca Slicer",
      anycubic: "Anycubic Next"
    };
    return names[slicer as keyof typeof names] || slicer;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Preferences - 3DP Hub</title>
        <meta name="description" content="Manage your account settings, preferences, and customize your 3DP Hub experience." />
      </Helmet>
      
      <Header 
        pageName="Preferences"
        useBackButton={true}
      />
      
      <div className="container max-w-4xl py-4 pt-[64px]">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle>Preferences</CardTitle>
            <CardDescription>
              Manage your account settings and application preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="account" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="account">Account</TabsTrigger>
                <TabsTrigger value="printers">My Printers</TabsTrigger>
                <TabsTrigger value="preferences">App Settings</TabsTrigger>
              </TabsList>
              
              <TabsContent value="account" className="mt-4 space-y-4">
                {/* Profile Section - Horizontal layout for username and profile picture */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <UsernameSettings />
                  </div>
                  <div className="space-y-4">
                    <ProfilePictureSettings />
                  </div>
                </div>
                
                {/* Privacy Setting - Compact */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <SimplePrivacyToggle />
                </div>
                
                {/* Password Settings - Compact */}
                <div className="space-y-3">
                  <h3 className="text-base font-medium">Password</h3>
                  <SecuritySettings />
                </div>
              </TabsContent>
              
              <TabsContent value="printers" className="mt-4">
                <PrinterManagement />
              </TabsContent>
              
              <TabsContent value="preferences" className="mt-4">
                <div className="space-y-4">
                  <div>
                    <CardTitle className="text-lg mb-3">Application Settings</CardTitle>
                  </div>
                  
                  {/* Compact settings grid */}
                  <div className="space-y-3">
                    {/* Preferred Slicer */}
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <Label className="text-sm font-medium">Preferred Slicer</Label>
                        {savedSlicer && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {getSlicerDisplayName(savedSlicer)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={savedSlicer || ""} onValueChange={handleSlicerChange}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select slicer" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="creality">Creality Print</SelectItem>
                            <SelectItem value="orca">Orca Slicer</SelectItem>
                            <SelectItem value="anycubic">Anycubic Next</SelectItem>
                          </SelectContent>
                        </Select>
                        {savedSlicer && (
                          <Button
                            onClick={clearSlicerPreference}
                            variant="outline"
                            size="sm"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Mode Preference */}
                    {savedMode && (
                      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <Label className="text-sm font-medium">Calculator Mode</Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {getModeDisplayName(savedMode)}
                          </p>
                        </div>
                        <Button
                          onClick={clearModePreference}
                          variant="outline"
                          size="sm"
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Clear
                        </Button>
                      </div>
                    )}

                    {/* Calibration Progress Reset */}
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <Label className="text-sm font-medium">Calibration Progress</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Reset all calibration data
                        </p>
                      </div>
                      <Button
                        onClick={resetCalibrationProgress}
                        variant="outline"
                        size="sm"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PreferencesPage;
